import { useMemo } from 'react';

import { useIntl } from '@zenbusiness/application-commons-intl';

import { RA_TASK_TYPES } from '#client/constants/partnerTasks';
import { useBusinessEntityInfo } from '#client/hooks/useBusinessEntityInfo';
import { useOrderTrackerStatus } from '#client/hooks/useOrderTrackerStatus';
import { formatEIN } from '#client/pages/BusinessProfile/helpers';

// formatEIN requires an fm (formatMessage) function for its null fallback;
// we handle the null case ourselves and pass a no-op for the formatting path.
const displayEIN = (ein, pendingLabel) => (ein ? formatEIN(ein, () => '') : pendingLabel);

function usePartnerProfileStatus({ partner } = {}) {
  const { formatMessage } = useIntl();
  const { loading: orderLoading, orderTasksWithStatus } = useOrderTrackerStatus();
  const {
    businessEntityInfo, loading: entityLoading, hasError
  } = useBusinessEntityInfo();

  const statusItems = useMemo(() => {
    const pendingLabel = formatMessage({ id: 'dashboard.partnerHome.profileHeader.pendingValue' });

    const taskMap = orderTasksWithStatus.reduce((acc, task) => {
      acc[task.type] = task;
      return acc;
    }, {});

    const raTask = RA_TASK_TYPES.map((type) => taskMap[type]).find(Boolean);
    const raActive = raTask?.status === 'active';

    return [
      {
        label: formatMessage({ id: 'dashboard.partnerHome.profileHeader.registeredAgentLabel' }),
        value: raActive
          ? formatMessage({ id: 'dashboard.partnerHome.profileHeader.zenBusinessValue' })
          : pendingLabel,
        isPending: !raActive
      },
      {
        label: formatMessage({ id: 'dashboard.partnerHome.profileHeader.einLabel' }),
        value: displayEIN(businessEntityInfo?.ein, pendingLabel),
        copyable: true,
        isPending: !businessEntityInfo?.ein
      },
      {
        label: formatMessage({ id: 'dashboard.partnerHome.profileHeader.planLabel' }),
        value: partner?.name,
        isPartnerPlan: true
      }
    ];
  }, [partner, orderTasksWithStatus, businessEntityInfo, formatMessage]);

  return {
    statusItems,
    loading: orderLoading || entityLoading,
    hasError,
    name: businessEntityInfo?.name,
    nameInitials: businessEntityInfo?.nameInitials,
    formationDate: businessEntityInfo?.formationDate
  };
}

export { usePartnerProfileStatus };
